/**
 * PayLater — public page /pagar/:token
 *
 * Passos:
 *   1. GET /api/pay/link/:token → obtém estado + métodos activados.
 *   2. Renderiza o resumo (STK, contagem, total, expiração).
 *   3. Mostra os métodos de pagamento configurados via <PaymentMethodPills/>.
 *   4. Estados dedicados: expirado, já pago, token usado, link inválido.
 *
 * Fluxo de checkout (Fase MOCK, Feb 2026):
 *   O admin ainda não integrou as SDKs reais Stripe/PayPal/ifthenpay, mas os
 *   botões já estão activos: cliquem-nos e o backend faz mock-checkout
 *   (marca a inscrição como paga, dispara email de confirmação, consome o
 *   token). Após sucesso, mostramos um toast e redireccionamos para "/" ao
 *   fim de ~1.8s. Quando as SDKs reais forem ligadas, o handler no backend
 *   passa a redireccionar-nos para o provider e o marcar-pago acontece no
 *   webhook.
 */
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Clock, CheckCircle2, XCircle, Copy, Landmark } from "lucide-react";
import SiteChrome from "@/components/SiteChrome";
import PaymentMethodPills from "./components/PaymentMethodPills";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v, currency = "EUR") => {
  const n = Number(v || 0);
  return currency === "EUR" ? `${n.toFixed(2).replace(".", ",")} €` : `${n.toFixed(2)} ${currency}`;
};

const T = {
  pt: {
    title: "Pagamento da inscrição",
    summary: "Resumo",
    school: "Escola",
    credentials: "Credenciais",
    unit: "Preço unitário",
    total: "Total a pagar",
    expires: "Link válido até",
    methods: "Escolhe um método de pagamento",
    bank_title: "Transferência bancária",
    bank_hint: "Envia o comprovativo para a escola confirmar o pagamento manualmente.",
    copied: "Copiado",
    processing: "A processar pagamento…",
    success: "Pagamento confirmado! Vais receber um email. A redireccionar…",
    cancelled: "Pagamento cancelado",
    cancelled_msg: "Não realizaste o pagamento na plataforma. A inscrição continua por pagar — podes tentar novamente a partir do link enviado por email.",
    cancelled_redirect: "A regressar à página inicial em",
    expired: "Este link expirou",
    expired_msg: "Contacta o suporte ou pede uma nova referência à tua escola.",
    used: "Este link já foi usado",
    paid: "Esta inscrição já está paga 🎉",
    invalid: "Link inválido ou não encontrado",
    back: "Voltar",
  },
  en: {
    title: "Enrolment payment",
    summary: "Summary",
    school: "School",
    credentials: "Credentials",
    unit: "Unit price",
    total: "Total due",
    expires: "Link valid until",
    methods: "Choose a payment method",
    bank_title: "Bank transfer",
    bank_hint: "Send the receipt to your school so the payment can be manually confirmed.",
    copied: "Copied",
    processing: "Processing payment…",
    success: "Payment confirmed! Check your email. Redirecting…",
    cancelled: "Payment cancelled",
    cancelled_msg: "You didn't complete the payment on the platform. The enrolment is still unpaid — you can try again from the link sent to your email.",
    cancelled_redirect: "Returning to home in",
    expired: "This link has expired",
    expired_msg: "Contact support or ask your school for a new reference.",
    used: "This link has already been used",
    paid: "This enrolment is already paid 🎉",
    invalid: "Invalid or unknown link",
    back: "Back",
  },
  es: {
    title: "Pago de la inscripción",
    summary: "Resumen",
    school: "Escuela",
    credentials: "Credenciales",
    unit: "Precio unitario",
    total: "Total a pagar",
    expires: "Enlace válido hasta",
    methods: "Elige un método de pago",
    bank_title: "Transferencia bancaria",
    bank_hint: "Envía el comprobante a la escuela para confirmar el pago manualmente.",
    copied: "Copiado",
    processing: "Procesando pago…",
    success: "¡Pago confirmado! Revisa tu email. Redirigiendo…",
    cancelled: "Pago cancelado",
    cancelled_msg: "No has completado el pago en la plataforma. La inscripción sigue sin pagar — puedes volver a intentarlo desde el enlace enviado por email.",
    cancelled_redirect: "Volviendo al inicio en",
    expired: "Este enlace ha caducado",
    expired_msg: "Contacta con soporte o pide una nueva referencia a tu escuela.",
    used: "Este enlace ya se ha utilizado",
    paid: "Esta inscripción ya está pagada 🎉",
    invalid: "Enlace inválido o no encontrado",
    back: "Volver",
  },
};

function CopyRow({ label, value, onCopy }) {
  return (
    <div className="flex items-center justify-between gap-2 py-1 border-b border-slate-100 last:border-0">
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className="font-mono text-sm text-slate-900 truncate">{value}</div>
      </div>
      <button
        type="button"
        onClick={() => onCopy(value)}
        className="text-slate-500 hover:text-slate-900 shrink-0 p-1.5 rounded hover:bg-slate-100"
        title="Copy"
      >
        <Copy className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function PayLater() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingKey, setProcessingKey] = useState(null);
  const [paid, setPaid] = useState(false);
  // For non-redirect flows (MB WAY / Multibanco / Bank transfer) we render
  // an inline "next steps" panel below the pills instead of navigating away.
  const [postState, setPostState] = useState(null); // {kind, ...data}
  // Payment cancelled by the user at the external gateway (Stripe/PayPal
  // cancel_url or PayPal capture failure). Shows dedicated screen +
  // auto-redirects home after 4s.
  const [cancelled, setCancelled] = useState(false);
  const [countdown, setCountdown] = useState(4);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/pay/link/${token}`);
        setData(d);
        // Return-from-Stripe success case: URL is /pagar/:token?success=1
        // Webhook may take a couple of seconds to arrive; the /pay/link
        // endpoint above will already return status=already_paid the moment
        // the webhook fires, so we just render the paid screen when either
        // (a) query says success=1 (best-effort), or (b) data.status flags it.
        try {
          const sp = new URLSearchParams(window.location.search);
          if (sp.get("success") === "1") {
            setPaid(true);
            toast.success(t.success, { duration: 3500 });
          } else if (sp.get("cancelled") === "1") {
            setCancelled(true);
          }
        } catch { /* ignore */ }
      } catch (err) {
        setError(err?.response?.data?.detail || "Link inválido ou não encontrado");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const t = T[data?.lang || "pt"] || T.pt;

  // Countdown to home when the payment was cancelled/refused at the gateway.
  useEffect(() => {
    if (!cancelled) return;
    if (countdown <= 0) { navigate("/"); return; }
    const id = setTimeout(() => setCountdown((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [cancelled, countdown, navigate]);

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(String(value));
      toast.success(t.copied);
    } catch { /* ignore */ }
  };

  // Kick off checkout. Response shapes:
  //   • `{checkout_url}`  → Stripe / PayPal — redirect
  //   • `{waiting:true, request_id, message}` → MB WAY — user confirms on phone
  //   • `{entity, reference, amount}` → Multibanco — show ATM data
  //   • `{iban_pt, ...}` → Bank transfer — show IBAN
  // Nothing is ever marked paid here — only the gateway webhook does that.
  const handleSelect = async (methodKey) => {
    if (processingKey || paid) return;
    // MB WAY needs a phone number BEFORE calling the backend (ifthenpay push).
    let phone = null;
    if (methodKey === "mbway") {
      phone = window.prompt(
        data?.lang === "en" ? "Enter your MB WAY mobile number (9 digits):"
        : data?.lang === "es" ? "Introduce tu número MB WAY (9 dígitos):"
        : "Indica o teu número MB WAY (9 dígitos):",
        "",
      );
      if (!phone) return;
      phone = phone.trim().replace(/\s/g, "");
      if (!/^\d{9,15}$/.test(phone)) {
        toast.error(data?.lang === "en" ? "Invalid phone number" : "Telefone inválido");
        return;
      }
    }
    setProcessingKey(methodKey);
    toast.loading(t.processing, { id: "checkout" });
    try {
      const payload = { method: methodKey };
      if (phone) payload.phone = phone;
      const { data: res } = await axios.post(`${API}/pay/link/${token}/checkout`, payload);
      toast.dismiss("checkout");
      if (res?.checkout_url) {
        window.location.href = res.checkout_url;
        return;
      }
      if (res?.waiting) {
        setPostState({ kind: "mbway_waiting", ...res });
        setProcessingKey(null);
        return;
      }
      if (res?.entity && res?.reference) {
        setPostState({ kind: "multibanco", ...res });
        setProcessingKey(null);
        return;
      }
      if (res?.iban_pt || res?.iban_intl) {
        setPostState({ kind: "bank", ...res });
        setProcessingKey(null);
        return;
      }
      if (res?.already_paid) {
        setPaid(true);
        return;
      }
      toast.error("Resposta inesperada do gateway");
      setProcessingKey(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro", { id: "checkout" });
      setProcessingKey(null);
    }
  };

  if (loading) return <SiteChrome><div className="p-8 text-center text-slate-500">…</div></SiteChrome>;

  if (error || !data) {
    return (
      <SiteChrome>
        <div className="max-w-md mx-auto mt-16 p-6 bg-white border border-slate-200 rounded-3xl shadow-sm text-center">
          <XCircle className="w-12 h-12 mx-auto text-rose-500 mb-3" />
          <h2 className="text-xl font-[Fredoka] font-bold text-slate-900">{T.pt.invalid}</h2>
          <p className="text-sm text-slate-600 mt-2">{error || "—"}</p>
          <Link to="/" className="mt-4 inline-block text-blue-600 underline">{T.pt.back}</Link>
        </div>
      </SiteChrome>
    );
  }

  if (data.status === "expired") {
    return (
      <SiteChrome>
        <div className="max-w-md mx-auto mt-16 p-6 bg-white border border-slate-200 rounded-3xl shadow-sm text-center" data-testid="pay-link-expired">
          <Clock className="w-12 h-12 mx-auto text-amber-500 mb-3" />
          <h2 className="text-xl font-[Fredoka] font-bold text-slate-900">{t.expired}</h2>
          <p className="text-sm text-slate-600 mt-2">{t.expired_msg}</p>
          <Link to="/" className="mt-4 inline-block text-blue-600 underline">{t.back}</Link>
        </div>
      </SiteChrome>
    );
  }

  if (data.status === "already_paid" || paid) {
    return (
      <SiteChrome>
        <div className="max-w-md mx-auto mt-16 p-6 bg-white border border-emerald-200 rounded-3xl shadow-sm text-center" data-testid="pay-link-paid">
          <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
          <h2 className="text-xl font-[Fredoka] font-bold text-emerald-900">{t.paid}</h2>
          <Link to="/" className="mt-4 inline-block text-emerald-700 underline">{t.back}</Link>
        </div>
      </SiteChrome>
    );
  }

  if (data.status === "used") {
    return (
      <SiteChrome>
        <div className="max-w-md mx-auto mt-16 p-6 bg-white border border-slate-200 rounded-3xl shadow-sm text-center">
          <CheckCircle2 className="w-12 h-12 mx-auto text-slate-400 mb-3" />
          <h2 className="text-xl font-[Fredoka] font-bold text-slate-900">{t.used}</h2>
          <Link to="/" className="mt-4 inline-block text-blue-600 underline">{t.back}</Link>
        </div>
      </SiteChrome>
    );
  }

  if (cancelled) {
    return (
      <SiteChrome>
        <div className="max-w-md mx-auto mt-16 p-6 bg-white border border-amber-200 rounded-3xl shadow-sm text-center" data-testid="pay-cancelled">
          <XCircle className="w-14 h-14 mx-auto text-amber-500 mb-3" />
          <h2 className="text-2xl font-[Fredoka] font-bold text-amber-900">{t.cancelled}</h2>
          <p className="text-sm text-slate-700 mt-3">{t.cancelled_msg}</p>
          <div className="mt-5 text-xs text-slate-500">
            {t.cancelled_redirect} <span className="font-mono font-bold text-slate-900" data-testid="pay-cancelled-countdown">{countdown}s</span>
          </div>
          <button
            onClick={() => navigate("/")}
            className="mt-4 inline-block bg-slate-900 hover:bg-slate-800 text-white rounded-full px-5 py-2 text-sm font-semibold"
            data-testid="pay-cancelled-home"
          >
            {t.back}
          </button>
        </div>
      </SiteChrome>
    );
  }

  const insc = data.inscription || {};
  const methods = data.methods || {};
  const lang = data.lang || "pt";
  const bank = methods.bank_transfer || {};

  return (
    <SiteChrome>
      <div className="max-w-3xl mx-auto p-4 md:p-8 space-y-6" data-testid="pay-later-page">
        <div>
          <h1 className="text-2xl md:text-3xl font-[Fredoka] font-bold text-slate-900">{t.title}</h1>
          <p className="text-sm text-slate-600 mt-1"><strong>{insc.stk_id}</strong> · {insc.school}</p>
        </div>

        {/* Summary */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <div className="text-xs uppercase font-bold text-slate-500 mb-3">{t.summary}</div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div><div className="text-xs text-slate-500">{t.school}</div><div className="font-semibold">{insc.school}</div></div>
            <div><div className="text-xs text-slate-500">{t.credentials}</div><div className="font-semibold">{insc.count}</div></div>
            <div><div className="text-xs text-slate-500">{t.unit}</div><div className="font-mono">{fmt(insc.price_per_credential, insc.currency)}</div></div>
            <div><div className="text-xs text-slate-500">{t.expires}</div><div className="font-mono">{(data.expires_at || "").substring(0, 10)}</div></div>
          </div>
          <div className="mt-4 flex items-baseline justify-between border-t border-slate-100 pt-3">
            <div className="text-xs uppercase font-bold text-slate-500">{t.total}</div>
            <div className="text-2xl font-[Fredoka] font-bold text-emerald-700">{fmt(insc.total_amount, insc.currency)}</div>
          </div>
        </div>

        {/* Payment method pills */}
        <div>
          <h2 className="text-lg font-[Fredoka] font-bold text-slate-900 mb-3">{t.methods}</h2>
          <PaymentMethodPills
            lang={lang}
            methods={methods}
            onSelect={handleSelect}
            showPayLater={false}
            loadingKey={processingKey}
          />
        </div>

        {/* Non-redirect post-checkout state (MB WAY waiting / Multibanco reference / bank). */}
        {postState?.kind === "mbway_waiting" && (
          <div className="rounded-2xl border border-lime-300 bg-lime-50 p-5 shadow-sm text-center" data-testid="post-mbway">
            <div className="text-4xl mb-2">📱</div>
            <div className="font-[Fredoka] font-bold text-lime-900 text-lg">MB WAY</div>
            <div className="text-sm text-lime-900 mt-2">{postState.message}</div>
            <div className="text-[11px] text-lime-700 mt-3">
              {lang === "en" ? "Once confirmed on your phone, you'll get an email and this page will refresh." :
               lang === "es" ? "Al confirmar en el móvil recibirás un email y esta página se actualizará." :
               "Assim que confirmares no telemóvel recebes email e esta página actualiza."}
            </div>
          </div>
        )}
        {postState?.kind === "multibanco" && (
          <div className="rounded-2xl border border-pink-300 bg-pink-50 p-5 shadow-sm" data-testid="post-multibanco">
            <div className="flex items-center gap-2 mb-3">
              <Landmark className="w-5 h-5 text-pink-700" />
              <div className="font-[Fredoka] font-bold text-pink-900 text-lg">Multibanco / Payshop</div>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-white rounded-xl p-3">
                <div className="text-[11px] uppercase text-slate-500">Entidade</div>
                <div className="font-mono font-bold text-xl text-slate-900">{postState.entity}</div>
              </div>
              <div className="bg-white rounded-xl p-3">
                <div className="text-[11px] uppercase text-slate-500">Referência</div>
                <div className="font-mono font-bold text-xl text-slate-900">{postState.reference}</div>
              </div>
              <div className="bg-white rounded-xl p-3">
                <div className="text-[11px] uppercase text-slate-500">Valor</div>
                <div className="font-mono font-bold text-xl text-slate-900">{postState.amount} €</div>
              </div>
            </div>
            <p className="text-xs text-slate-600 mt-3">{postState.message}</p>
          </div>
        )}
        {postState?.kind === "bank" && (
          <div className="rounded-2xl border border-slate-300 bg-white p-5 shadow-sm" data-testid="post-bank">
            <div className="flex items-center gap-2 mb-3">
              <Landmark className="w-5 h-5 text-slate-700" />
              <div className="font-[Fredoka] font-bold text-slate-900 text-lg">{t.bank_title}</div>
            </div>
            <div className="space-y-1 text-xs">
              <CopyRow label="IBAN" value={lang === "pt" ? postState.iban_pt : (postState.iban_intl || postState.iban_pt)} onCopy={copy} />
              {postState.bic && <CopyRow label="SWIFT / BIC" value={postState.bic} onCopy={copy} />}
              {postState.beneficiary && <CopyRow label="Beneficiário" value={postState.beneficiary} onCopy={copy} />}
              <CopyRow label="Referência" value={postState.reference} onCopy={copy} />
            </div>
            <p className="text-xs text-slate-600 mt-3">{postState.message}</p>
          </div>
        )}

        {/* Bank transfer (kept as a detailed section because it needs IBAN copy-rows) */}
        {bank.enabled && (bank.iban_pt || bank.iban_intl) && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="method-bank">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-slate-100 grid place-items-center text-slate-700">
                <Landmark className="w-5 h-5" />
              </div>
              <div>
                <div className="font-bold text-slate-900">{t.bank_title}</div>
                <div className="text-xs text-slate-500">IBAN · SWIFT/BIC</div>
              </div>
            </div>
            <div className="space-y-1 text-xs">
              {(bank.iban_pt || bank.iban_intl) && (
                <CopyRow label="IBAN" value={lang === "pt" ? bank.iban_pt : (bank.iban_intl || bank.iban_pt)} onCopy={copy} />
              )}
              {bank.bic && <CopyRow label="SWIFT / BIC" value={bank.bic} onCopy={copy} />}
              {bank.beneficiary && (
                <CopyRow label={lang === "pt" ? "Beneficiário" : lang === "es" ? "Beneficiario" : "Beneficiary"} value={bank.beneficiary} onCopy={copy} />
              )}
              <CopyRow label={lang === "pt" ? "Referência" : "Reference"} value={insc.stk_id} onCopy={copy} />
            </div>
            <p className="text-[11px] text-slate-500 italic mt-2">{t.bank_hint}</p>
          </div>
        )}
      </div>
    </SiteChrome>
  );
}
